import React, { useState, useEffect } from 'react';
import { openDB } from 'idb';
import {
  Box,
  Container,
  Typography,
  Paper,
  TextField,
  Button,
  Grid,
  Card,
  Alert,
  Snackbar,
  Divider,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Save as SaveIcon,
  Preview as PreviewIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { useTheme } from '../contexts/ThemeContext';

interface AboutUsContent {
  title: string;
  subtitle: string;
  missionTitle: string;
  missionContent: string;
  featuresTitle: string;
  features: {
    title: string;
    description: string;
    icon: string;
  }[];
  contactTitle: string;
  contactSubtitle: string;
  contactEmail: string;
}

// Default content
const defaultContent: AboutUsContent = {
  title: 'About Goat Cards',
  subtitle: 'Your premier destination for premium sports cards and collectibles',
  missionTitle: 'Our Mission',
  missionContent: 'At Goat Cards, we\'re passionate about connecting collectors with the finest sports cards and memorabilia. Our platform combines cutting-edge technology with deep market insights to provide you with the most comprehensive and reliable card collecting experience.',
  featuresTitle: 'What We Offer',
  features: [
    {
      title: 'Live Market Data',
      description: 'Real-time pricing and market trends from eBay and other major platforms, ensuring you always have the most current information.',
      icon: 'TrendingUp'
    },
    {
      title: 'Secure Transactions',
      description: 'Safe and secure payment processing with buyer protection, ensuring your transactions are always protected.',
      icon: 'Security'
    },
    {
      title: 'Expert Support',
      description: 'Dedicated customer support team with deep knowledge of the sports card market to assist you with any questions.',
      icon: 'Support'
    }
  ],
  contactTitle: 'Get in Touch',
  contactSubtitle: 'Have questions about our platform or need assistance? We\'re here to help you build your perfect collection.',
  contactEmail: 'support@goatcards.shop'
};

const AdminAboutUsEditor: React.FC = () => {
  const { mode } = useTheme();
  const [content, setContent] = useState<AboutUsContent>(defaultContent);
  const [originalContent, setOriginalContent] = useState<AboutUsContent>(defaultContent);
  const [isEditing, setIsEditing] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  // Load saved content on component mount
  useEffect(() => {
    const loadContent = async () => {
      let loadedContent = null;
      
      // Try to load from multiple sources in order of preference
      const sources = [
        () => localStorage.getItem('aboutUsContent'),
        () => sessionStorage.getItem('aboutUsContent'),
        () => localStorage.getItem('aboutUsContent_backup'),
        async () => {
          if ('indexedDB' in window) {
            try {
              const db = await openDB('goatCardsDB', 1);
              return await db.get('content', 'aboutUsContent');
            } catch (error) {
              console.warn('IndexedDB load failed:', error);
              return null;
            }
          }
          return null;
        }
      ];
      
      for (const source of sources) {
        try {
          const result = await source();
          if (result) {
            const parsedContent = typeof result === 'string' ? JSON.parse(result) : result;
            if (parsedContent && typeof parsedContent === 'object') {
              loadedContent = parsedContent;
              console.log('Content loaded from source:', source.name || 'unknown');
              break;
            }
          }
        } catch (error) {
          console.warn('Failed to load from source:', error);
          continue;
        }
      }
      
      if (loadedContent) {
        setContent(loadedContent);
        setOriginalContent(loadedContent);
        console.log('Content loaded successfully');
      } else {
        // If no saved content exists, save the default content
        console.log('No saved content found, using default content');
        setContent(defaultContent);
        setOriginalContent(defaultContent);
        localStorage.setItem('aboutUsContent', JSON.stringify(defaultContent));
      }
    };
    
    loadContent();
  }, []);

  const handleSave = async () => {
    try {
      console.log('Admin About Us Editor: Saving content:', content);
      
      // Save to multiple localStorage keys for redundancy
      localStorage.setItem('aboutUsContent', JSON.stringify(content));
      localStorage.setItem('aboutUsContent_backup', JSON.stringify(content));
      localStorage.setItem('aboutUsContent_timestamp', new Date().toISOString());
      
      // Save to sessionStorage as additional backup
      sessionStorage.setItem('aboutUsContent', JSON.stringify(content));
      
      // Save to IndexedDB for persistent storage (if available)
      if ('indexedDB' in window) {
        try {
          const db = await openDB('goatCardsDB', 1, {
            upgrade(db) {
              if (!db.objectStoreNames.contains('content')) {
                db.createObjectStore('content');
              }
            },
          });
          await db.put('content', content, 'aboutUsContent');
        } catch (dbError) {
          console.warn('IndexedDB not available, using localStorage only:', dbError);
        }
      }
      
      console.log('Admin About Us Editor: Content saved to multiple storage locations');
      
      // Update original content to current content
      setOriginalContent(content);
      
      // Dispatch custom event to notify About Us page of the update
      window.dispatchEvent(new CustomEvent('aboutUsContentUpdated'));
      console.log('Admin About Us Editor: Custom event dispatched');
      
      // Simulate API call delay
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setIsEditing(false);
      setShowSuccess(true);
      
      // In a real implementation, you would also save to your backend API here
      console.log('Saving About Us content to API:', content);
      
    } catch (error) {
      console.error('Error saving content:', error);
      setShowError(true);
    }
  };

  const handleCancel = () => {
    // Reset content to the last saved version
    setContent(originalContent);
    setIsEditing(false);
  };

  const updateFeature = (index: number, field: keyof typeof content.features[0], value: string) => {
    const newFeatures = [...content.features];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    setContent({ ...content, features: newFeatures });
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, color: mode === 'light' ? '#000000' : '#FFFFFF' }}>
          About Us Page Editor
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Tooltip title="Preview Page">
            <IconButton
              onClick={() => window.open('/about', '_blank')}
              sx={{ color: mode === 'light' ? '#000000' : '#FFFFFF' }}
            >
              <PreviewIcon />
            </IconButton>
          </Tooltip>
          <Button
            variant={isEditing ? "outlined" : "contained"}
            startIcon={isEditing ? <EditIcon /> : <EditIcon />}
            onClick={() => setIsEditing(!isEditing)}
            sx={{ fontWeight: 600 }}
          >
            {isEditing ? 'Cancel Editing' : 'Edit Content'}
          </Button>
        </Box>
      </Box>

      {/* Main Content */}
      <Grid container spacing={4}>
        {/* Editor Panel */}
        <Grid item xs={12} lg={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              background: mode === 'light' ? '#f8f9fa' : '#2d3748',
              borderRadius: 3,
              height: 'fit-content',
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: mode === 'light' ? '#000000' : '#FFFFFF' }}>
              Edit Content
            </Typography>

            {/* Hero Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: mode === 'light' ? '#000000' : '#FFFFFF' }}>
                Hero Section
              </Typography>
              <TextField
                fullWidth
                label="Page Title"
                value={content.title}
                onChange={(e) => setContent({ ...content, title: e.target.value })}
                disabled={!isEditing}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                }}
                InputLabelProps={{
                  sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                }}
              />
              <TextField
                fullWidth
                label="Subtitle"
                value={content.subtitle}
                onChange={(e) => setContent({ ...content, subtitle: e.target.value })}
                disabled={!isEditing}
                multiline
                rows={2}
                InputProps={{
                  sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                }}
                InputLabelProps={{
                  sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Mission Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: mode === 'light' ? '#000000' : '#FFFFFF' }}>
                Mission Section
              </Typography>
              <TextField
                fullWidth
                label="Mission Title"
                value={content.missionTitle}
                onChange={(e) => setContent({ ...content, missionTitle: e.target.value })}
                disabled={!isEditing}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                }}
                InputLabelProps={{
                  sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                }}
              />
              <TextField
                fullWidth
                label="Mission Content"
                value={content.missionContent}
                onChange={(e) => setContent({ ...content, missionContent: e.target.value })}
                disabled={!isEditing}
                multiline
                rows={4}
                InputProps={{
                  sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                }}
                InputLabelProps={{
                  sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                }}
              />
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Features Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: mode === 'light' ? '#000000' : '#FFFFFF' }}>
                Features Section
              </Typography>
              <TextField
                fullWidth
                label="Features Title"
                value={content.featuresTitle}
                onChange={(e) => setContent({ ...content, featuresTitle: e.target.value })}
                disabled={!isEditing}
                sx={{ mb: 3 }}
                InputProps={{
                  sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                }}
                InputLabelProps={{
                  sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                }}
              />
              
              {content.features.map((feature, index) => (
                <Card key={index} sx={{ mb: 2, p: 2 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, color: mode === 'light' ? '#000000' : '#FFFFFF' }}>
                    Feature {index + 1}
                  </Typography>
                  <TextField
                    fullWidth
                    label="Title"
                    value={feature.title}
                    onChange={(e) => updateFeature(index, 'title', e.target.value)}
                    disabled={!isEditing}
                    size="small"
                    sx={{ mb: 1 }}
                    InputProps={{
                      sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                    }}
                    InputLabelProps={{
                      sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Description"
                    value={feature.description}
                    onChange={(e) => updateFeature(index, 'description', e.target.value)}
                    disabled={!isEditing}
                    multiline
                    rows={2}
                    size="small"
                    InputProps={{
                      sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                    }}
                    InputLabelProps={{
                      sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                    }}
                  />
                </Card>
              ))}
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* Contact Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, color: mode === 'light' ? '#000000' : '#FFFFFF' }}>
                Contact Section
              </Typography>
              <TextField
                fullWidth
                label="Contact Title"
                value={content.contactTitle}
                onChange={(e) => setContent({ ...content, contactTitle: e.target.value })}
                disabled={!isEditing}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                }}
                InputLabelProps={{
                  sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                }}
              />
              <TextField
                fullWidth
                label="Contact Subtitle"
                value={content.contactSubtitle}
                onChange={(e) => setContent({ ...content, contactSubtitle: e.target.value })}
                disabled={!isEditing}
                multiline
                rows={2}
                sx={{ mb: 2 }}
                InputProps={{
                  sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                }}
                InputLabelProps={{
                  sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                }}
              />
              <TextField
                fullWidth
                label="Contact Email"
                value={content.contactEmail}
                onChange={(e) => setContent({ ...content, contactEmail: e.target.value })}
                disabled={!isEditing}
                InputProps={{
                  sx: { color: mode === 'light' ? '#000000' : '#FFFFFF' }
                }}
                InputLabelProps={{
                  sx: { color: mode === 'light' ? '#666666' : '#a0aec0' }
                }}
              />
            </Box>

            {/* Save Button */}
            {isEditing && (
              <Box sx={{ display: 'flex', gap: 2, mt: 4 }}>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSave}
                  sx={{ fontWeight: 600 }}
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Paper>
        </Grid>

        {/* Preview Panel */}
        <Grid item xs={12} lg={6}>
          <Paper
            elevation={2}
            sx={{
              p: 4,
              background: mode === 'light' ? '#f8f9fa' : '#2d3748',
              borderRadius: 3,
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 700, mb: 3, color: mode === 'light' ? '#000000' : '#FFFFFF' }}>
              Live Preview
            </Typography>

            {/* Hero Section Preview */}
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                  fontSize: { xs: '1.5rem', md: '2rem' },
                }}
              >
                {content.title}
              </Typography>
              <Typography
                variant="h6"
                sx={{
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                  lineHeight: 1.6,
                }}
              >
                {content.subtitle}
              </Typography>
            </Box>

            {/* Mission Section Preview */}
            <Card sx={{ mb: 3, p: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                }}
              >
                {content.missionTitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                  lineHeight: 1.6,
                }}
              >
                {content.missionContent}
              </Typography>
            </Card>

            {/* Features Preview */}
            <Box sx={{ mb: 3 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                }}
              >
                {content.featuresTitle}
              </Typography>
              <Grid container spacing={2}>
                {content.features.map((feature, index) => (
                  <Grid item xs={12} key={index}>
                    <Card sx={{ p: 2 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          mb: 1,
                          color: mode === 'light' ? '#000000' : '#FFFFFF',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: mode === 'light' ? '#000000' : '#FFFFFF',
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>

            {/* Contact Preview */}
            <Box sx={{ textAlign: 'center' }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 600,
                  mb: 2,
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                }}
              >
                {content.contactTitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                  mb: 2,
                }}
              >
                {content.contactSubtitle}
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: mode === 'light' ? '#000000' : '#FFFFFF',
                  fontWeight: 600,
                }}
              >
                {content.contactEmail}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Success/Error Messages */}
      <Snackbar
        open={showSuccess}
        autoHideDuration={6000}
        onClose={() => setShowSuccess(false)}
      >
        <Alert onClose={() => setShowSuccess(false)} severity="success">
          About Us page content saved successfully!
        </Alert>
      </Snackbar>

      <Snackbar
        open={showError}
        autoHideDuration={6000}
        onClose={() => setShowError(false)}
      >
        <Alert onClose={() => setShowError(false)} severity="error">
          Error saving content. Please try again.
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default AdminAboutUsEditor; 